import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	forwardRef,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import {MatSelect, MatSelectChange} from "@angular/material/select";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {Subject} from "rxjs";
import {debounceTime, distinctUntilChanged, takeUntil} from "rxjs/operators";

const MULTISELECT_VALUE_ACCESSOR: any = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => MultiSelectComponent),
	multi: true,
};

interface SelectOption {
	id: string;
	name: string;
}

/** Number of options to show per page */
const PAGE_SIZE = 20;

@Component({
	selector: 'multi-select',
	templateUrl: './multi-select.component.html',
	styleUrls: ['./multi-select.component.scss'],
	providers: [MULTISELECT_VALUE_ACCESSOR],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiSelectComponent implements OnChanges, OnDestroy, ControlValueAccessor {

	@Input() label: string | undefined;
	@Input() placeholder: string | undefined;
	@Input() options: SelectOption[] = [];
	@Input() disabled: boolean = false;
	@Input() extraClass: string = '';
	@Input() searchable: boolean = false;

	@Output() onSelectionChange = new EventEmitter<string[]>();

	@ViewChild('selectElement') selectElement: MatSelect | undefined;
	@ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement> | undefined;

	/** Current selected value(s) */
	value: string[] = [];

	/** Current search input value */
	searchValue: string = '';

	/** Filtered and limited options for rendering */
	renderedOptions: SelectOption[] = [];

	/** Hidden selected options (not in current page but need to be in DOM for MatSelect) */
	hiddenSelectedOptions: SelectOption[] = [];

	/** Total count of filtered options (before limit) */
	totalFilteredCount: number = 0;

	/** Tooltip text for selected values */
	tooltipText: string = '';

	/** Display text for selected values (shown when dropdown is closed) */
	displayText: string = '';

	/** Current page (1-based) */
	currentPage: number = 1;

	/** Total number of pages */
	totalPages: number = 1;

	/** Page size */
	readonly pageSize = PAGE_SIZE;

	/** Whether the dropdown is currently open */
	private isDropdownOpen: boolean = false;

	/** Subject for search input with debounce */
	private readonly searchSubject = new Subject<string>();

	/** Subject for component destruction cleanup */
	private readonly destroy$ = new Subject<void>();

	/** Cache for filtered results */
	private filterCache = new Map<string, SelectOption[]>();

	/** Cached filtered options for pagination */
	private currentFilteredOptions: SelectOption[] = [];

	constructor(private readonly cdr: ChangeDetectorRef) {
		this.setupSearchDebounce();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['options']) {
			const newOptions = changes['options'].currentValue;
			if (Array.isArray(newOptions)) {
				this.filterCache.clear();
				this.currentPage = 1;
				this.applyFilter(this.searchValue);
				this.updateTooltip();
			}
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.searchSubject.complete();
		this.filterCache.clear();
	}

	/**
	 * Sets up debounced search to avoid filtering on every keystroke
	 */
	private setupSearchDebounce(): void {
		this.searchSubject.pipe(
			debounceTime(150),
			distinctUntilChanged(),
			takeUntil(this.destroy$)
		).subscribe(searchTerm => {
			this.currentPage = 1;
			this.applyFilter(searchTerm);
			this.cdr.markForCheck();
		});
	}

	/**
	 * Applies filter to options and calculates pagination.
	 * Always filters from original options array (not from previously filtered results).
	 * @param searchTerm - the search term to filter by
	 * @param sortSelectedFirst - if true, selected items are placed at the beginning (only on dropdown open)
	 */
	private applyFilter(searchTerm: string, sortSelectedFirst: boolean = false): void {
		const normalizedTerm = (searchTerm || '').trim().toUpperCase();

		let filteredOptions: SelectOption[];

		// Filter from original options
		if (!normalizedTerm) {
			filteredOptions = this.options ? [...this.options] : [];
		} else {
			filteredOptions = (this.options || []).filter(option =>
				option.id?.toUpperCase().includes(normalizedTerm) ||
				option.name?.toUpperCase().includes(normalizedTerm)
			);
		}

		// Sort: selected items first, then the rest (only when opening dropdown)
		if (sortSelectedFirst) {
			const selectedSet = new Set(this.value || []);
			if (selectedSet.size > 0) {
				const selectedOptions = filteredOptions.filter(opt => selectedSet.has(opt.id));
				const nonSelectedOptions = filteredOptions.filter(opt => !selectedSet.has(opt.id));
				filteredOptions = [...selectedOptions, ...nonSelectedOptions];
			}
		}

		// Store filtered options for pagination
		this.currentFilteredOptions = filteredOptions;
		this.totalFilteredCount = filteredOptions.length;

		// Calculate total pages
		this.totalPages = Math.max(1, Math.ceil(filteredOptions.length / PAGE_SIZE));

		// Ensure current page is valid
		if (this.currentPage > this.totalPages) {
			this.currentPage = this.totalPages;
		}

		// Apply pagination
		this.updateRenderedOptions();
	}

	/**
	 * Updates rendered options based on current page.
	 * Also calculates hidden selected options that need to stay in DOM.
	 */
	private updateRenderedOptions(): void {
		const startIndex = (this.currentPage - 1) * PAGE_SIZE;
		const endIndex = startIndex + PAGE_SIZE;

		this.renderedOptions = this.currentFilteredOptions.slice(startIndex, endIndex);

		// Find selected options that are NOT in the current page
		// These need to be in DOM (hidden) for MatSelect to maintain selection
		const renderedIds = new Set(this.renderedOptions.map(opt => opt.id));
		const selectedSet = new Set(this.value || []);

		this.hiddenSelectedOptions = (this.options || []).filter(opt =>
			selectedSet.has(opt.id) && !renderedIds.has(opt.id)
		);
	}

	/**
	 * Navigate to first page
	 */
	goToFirstPage(event: MouseEvent): void {
		event.stopPropagation();
		if (this.currentPage !== 1) {
			this.currentPage = 1;
			this.updateRenderedOptions();
			this.scrollPanelToTop();
			this.cdr.markForCheck();
		}
	}

	/**
	 * Navigate to previous page
	 */
	goToPreviousPage(event: MouseEvent): void {
		event.stopPropagation();
		if (this.currentPage > 1) {
			this.currentPage--;
			this.updateRenderedOptions();
			this.scrollPanelToTop();
			this.cdr.markForCheck();
		}
	}

	/**
	 * Navigate to next page
	 */
	goToNextPage(event: MouseEvent): void {
		event.stopPropagation();
		if (this.currentPage < this.totalPages) {
			this.currentPage++;
			this.updateRenderedOptions();
			this.scrollPanelToTop();
			this.cdr.markForCheck();
		}
	}

	/**
	 * Navigate to last page
	 */
	goToLastPage(event: MouseEvent): void {
		event.stopPropagation();
		if (this.currentPage !== this.totalPages) {
			this.currentPage = this.totalPages;
			this.updateRenderedOptions();
			this.scrollPanelToTop();
			this.cdr.markForCheck();
		}
	}

	/**
	 * Scrolls the panel to the top
	 */
	private scrollPanelToTop(): void {
		setTimeout(() => {
			const panel = this.selectElement?.panel?.nativeElement as HTMLElement;
			if (panel) {
				panel.scrollTop = 0;
			}
		});
	}

	/**
	 * Handles search input changes with debounce
	 */
	onSearchValueChange(event: string): void {
		this.searchValue = event || '';
		this.searchSubject.next(this.searchValue);
	}

	/**
	 * Clears the search input and resets filter
	 */
	clearSearch(): void {
		this.searchValue = '';
		this.searchSubject.next('');
	}

	/**
	 * Handles selection change from MatSelect
	 */
	onMatSelectionChange(event: MatSelectChange): void {
		const selectedValues = event.value as string[];
		this.value = selectedValues;
		this.onChange(selectedValues);
		this.onSelectionChange.emit(selectedValues);
		this.updateTooltip();
		// Only update hidden options to keep selections in DOM, don't reorder
		this.updateHiddenSelectedOptions();
		this.cdr.markForCheck();
	}

	/**
	 * Updates only the hidden selected options without reordering the list
	 */
	private updateHiddenSelectedOptions(): void {
		const renderedIds = new Set(this.renderedOptions.map(opt => opt.id));
		const selectedSet = new Set(this.value || []);

		this.hiddenSelectedOptions = (this.options || []).filter(opt =>
			selectedSet.has(opt.id) && !renderedIds.has(opt.id)
		);
	}

	/**
	 * Handles dropdown open/close events
	 */
	onOpenedChange(isOpen: boolean): void {
		this.isDropdownOpen = isOpen;

		if (isOpen) {
			// Reset to first page when opening
			this.currentPage = 1;
			// Re-apply filter when opening, sorting selected items first
			this.applyFilter(this.searchValue, true);
			// Reset scroll position
			this.scrollPanelToTop();
			// Focus search input
			setTimeout(() => {
				this.searchInput?.nativeElement?.focus();
			});
		} else {
			// Clear search when closing
			if (this.searchValue) {
				this.clearSearch();
			}
		}
	}

	/**
	 * Updates tooltip and display text based on selected values
	 */
	private updateTooltip(): void {
		if (!this.value?.length || !this.options?.length) {
			this.tooltipText = '';
			this.displayText = '';
			return;
		}

		const selectedNames = this.value
		.map(id => this.options.find(opt => opt.id === id)?.name)
		.filter(Boolean);

		this.tooltipText = selectedNames.join(', ');

		// Display text: show names if few, otherwise show count
		if (selectedNames.length <= 2) {
			this.displayText = selectedNames.join(', ');
		} else {
			this.displayText = `${selectedNames.length} selected`;
		}
	}

	/**
	 * TrackBy function for ngFor optimization
	 */
	trackByOptionId(index: number, option: SelectOption): string {
		return option.id;
	}

	/**
	 * Check if an option is selected (for highlighting on current page)
	 */
	isSelected(optionId: string): boolean {
		return this.value?.includes(optionId) ?? false;
	}

	// ControlValueAccessor implementation
	onChange: (value: string[]) => void = () => {};
	onTouched: () => void = () => {};

	writeValue(value: string | string[]): void {
		if (value === null || value === undefined) {
			this.value = [];
		} else if (Array.isArray(value)) {
			this.value = value;
		} else {
			this.value = [value];
		}
		this.updateTooltip();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: string[]) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}
}
